/**
 * HamBestSpots - Score Calculator
 * Two distinct scoring systems for "Sommet" (Summit) and "Confort" (Comfort) spots
 * Includes LOS (Line of Sight) calculation on 8 cardinal directions
 */

// 8 cardinal directions in degrees (0 = North)
const CARDINAL_DIRECTIONS = [
  { name: 'N', angle: 0 },
  { name: 'NE', angle: 45 },
  { name: 'E', angle: 90 },
  { name: 'SE', angle: 135 },
  { name: 'S', angle: 180 },
  { name: 'SW', angle: 225 },
  { name: 'W', angle: 270 },
  { name: 'NW', angle: 315 },
];

/**
 * Calculate score for a Summit spot
 * Prioritizes: altitude, LOS clearance, radio silence
 */
export const calculateSummitScore = (point, qrmData, allPoints) => {
  const altitudeScore = calculateAltitudeScore(point, allPoints);
  const losData = calculateLOSScore(point, allPoints);
  const qrmScore = calculateQRMScore(qrmData);

  // Summit weighting: Altitude 40%, LOS 45%, QRM 15%
  const weightedScore = (altitudeScore * 0.40) + (losData.score * 0.45) + (qrmScore * 0.15);

  return {
    score: Math.max(0, Math.min(100, weightedScore)),
    losData: losData
  };
};

/**
 * Calculate LOS (Line of Sight) score based on neighboring summits
 * Checks clearance in 8 cardinal directions
 */
export const calculateLOSScore = (point, allPoints) => {
  if (!allPoints || allPoints.length < 2) {
    return { score: 70, directions: {}, clearCount: 8 };
  }

  const pointElevation = point.elevation || 0;
  const directions = {};
  let totalScore = 0;
  let clearCount = 0;

  CARDINAL_DIRECTIONS.forEach(dir => {
    // Find points in this direction (within 30° cone)
    const pointsInDirection = allPoints.filter(p => {
      if (p.lat === point.lat && p.lng === point.lng) return false;
      
      const bearing = calculateBearing(point.lat, point.lng, p.lat, p.lng);
      const angleDiff = Math.abs(normalizeAngle(bearing - dir.angle));
      
      return angleDiff <= 30 || angleDiff >= 330;
    });

    if (pointsInDirection.length === 0) {
      // No obstacles in this direction = clear
      directions[dir.name] = { clear: true, angle: -5, distance: null };
      totalScore += 100;
      clearCount++;
    } else {
      // Find the closest point that could be an obstacle
      let worstAngle = -90; // Start with best case (looking down)
      let obstacleDistance = null;

      pointsInDirection.forEach(obstacle => {
        const distance = getDistance(point.lat, point.lng, obstacle.lat, obstacle.lng);
        if (distance < 0.5) return; // Ignore very close points
        
        const obstacleElevation = obstacle.elevation || 0;
        const elevationDiff = obstacleElevation - pointElevation;
        
        // Calculate elevation angle in degrees
        // angle = atan(elevation_diff / distance)
        const angleRad = Math.atan2(elevationDiff, distance * 1000);
        const angleDeg = angleRad * (180 / Math.PI);
        
        if (angleDeg > worstAngle) {
          worstAngle = angleDeg;
          obstacleDistance = distance;
        }
      });

      // Score based on elevation angle
      // Negative angle = clear horizon (good)
      // Positive angle = obstacle (bad)
      let dirScore;
      if (worstAngle <= -2) {
        dirScore = 100; // Excellent clearance
        clearCount++;
        directions[dir.name] = { clear: true, angle: worstAngle, distance: obstacleDistance };
      } else if (worstAngle <= 0) {
        dirScore = 85; // Good clearance
        clearCount++;
        directions[dir.name] = { clear: true, angle: worstAngle, distance: obstacleDistance };
      } else if (worstAngle <= 2) {
        dirScore = 60; // Marginal
        directions[dir.name] = { clear: false, angle: worstAngle, distance: obstacleDistance };
      } else if (worstAngle <= 5) {
        dirScore = 40; // Obstructed
        directions[dir.name] = { clear: false, angle: worstAngle, distance: obstacleDistance };
      } else {
        dirScore = 20; // Heavily obstructed
        directions[dir.name] = { clear: false, angle: worstAngle, distance: obstacleDistance };
      }
      
      totalScore += dirScore;
    }
  });

  return {
    score: totalScore / 8,
    directions: directions,
    clearCount: clearCount
  };
};

/**
 * Calculate bearing between two points
 */
const calculateBearing = (lat1, lng1, lat2, lng2) => {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  
  const x = Math.sin(dLng) * Math.cos(lat2Rad);
  const y = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  
  let bearing = Math.atan2(x, y) * 180 / Math.PI;
  return (bearing + 360) % 360;
};

/**
 * Normalize angle to 0-360 range
 */
const normalizeAngle = (angle) => {
  return ((angle % 360) + 360) % 360;
};

/**
 * Calculate score for a Comfort spot
 * Prioritizes: amenities, accessibility, LOS clearance
 */
export const calculateComfortScore = (point, amenityData, allPoints) => {
  const amenityScore = calculateAmenityScore(amenityData);
  const accessScore = calculateAccessibilityScore(amenityData);
  const losData = calculateLOSScore(point, allPoints);

  // Comfort weighting: Amenities 35%, Accessibility 30%, LOS 35%
  const weightedScore = (amenityScore * 0.35) + (accessScore * 0.30) + (losData.score * 0.35);

  return {
    score: Math.max(0, Math.min(100, weightedScore)),
    losData: losData
  };
};

/**
 * Calculate altitude score relative to other points
 */
const calculateAltitudeScore = (point, allPoints) => {
  if (!allPoints || allPoints.length === 0) return 50;

  const elevations = allPoints.map(p => p.elevation || 0);
  const minElevation = Math.min(...elevations);
  const maxElevation = Math.max(...elevations);

  if (maxElevation === minElevation) return 50;

  const normalized = ((point.elevation - minElevation) / (maxElevation - minElevation)) * 100;
  return normalized;
};

/**
 * Calculate QRM (radio interference) score
 */
const calculateQRMScore = (qrmData) => {
  let score = 100;

  if (!qrmData) return score;

  if (qrmData.hasBroadcastTransmitters) score -= 30;
  if (qrmData.isUrban) score -= 25;
  if (qrmData.hasHighVoltageLine) score -= 15;

  return Math.max(0, score);
};

/**
 * Calculate amenity score for comfort spots
 */
const calculateAmenityScore = (amenityData) => {
  if (!amenityData) return 50;

  let score = 50; // Base score

  // Add points for each amenity
  if (amenityData.hasShelter) score += 15;
  if (amenityData.hasToilets) score += 10;
  if (amenityData.hasWater) score += 10;
  if (amenityData.hasViewpoint) score += 10;
  if (amenityData.hasBbq) score += 5;

  return Math.min(100, score);
};

/**
 * Calculate accessibility score
 */
const calculateAccessibilityScore = (amenityData) => {
  if (!amenityData) return 50;

  let score = 40; // Base score

  if (amenityData.hasRoadAccess) score += 30;
  if (amenityData.hasParking) score += 25;
  if (amenityData.hasTrail) score += 5;

  return Math.min(100, score);
};

/**
 * Calculate altitude bonus for comfort spots
 * Higher picnic areas get a small bonus
 */
const calculateAltitudeBonus = (point, allPoints) => {
  if (!point.elevation || !allPoints || allPoints.length === 0) return 50;

  const elevations = allPoints.map(p => p.elevation || 0);
  const avgElevation = elevations.reduce((sum, e) => sum + e, 0) / elevations.length;

  // Give bonus if above average elevation
  if (point.elevation > avgElevation * 1.5) return 100;
  if (point.elevation > avgElevation * 1.2) return 80;
  if (point.elevation > avgElevation) return 65;
  return 50;
};

/**
 * Get distance between two points in km
 */
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Sort and filter summit spots - return top N by score
 */
export const filterTopSummits = (summits, minCount = 5) => {
  if (!summits || summits.length === 0) return [];
  
  // Sort by score descending
  const sorted = [...summits].sort((a, b) => b.score - a.score);
  
  // Return at least minCount or all if less available
  return sorted.slice(0, Math.max(minCount, Math.min(sorted.length, 10)));
};

/**
 * Sort and filter comfort spots - return top N by score
 */
export const filterTopComforts = (comforts, minCount = 5) => {
  if (!comforts || comforts.length === 0) return [];
  
  // Sort by score descending
  const sorted = [...comforts].sort((a, b) => b.score - a.score);
  
  // Return at least minCount or all if less available
  return sorted.slice(0, Math.max(minCount, Math.min(sorted.length, 10)));
};

/**
 * Get score color based on value
 */
export const getScoreColor = (score) => {
  if (score >= 80) return '#10b981'; // Emerald
  if (score >= 70) return '#84cc16'; // Lime
  if (score >= 60) return '#f59e0b'; // Amber
  if (score >= 50) return '#fb923c'; // Orange
  return '#94a3b8'; // Gray
};

/**
 * Get score label based on value
 */
export const getScoreLabel = (score) => {
  if (score >= 80) return 'Excellent';
  if (score >= 70) return 'Très Bon';
  if (score >= 60) return 'Bon';
  if (score >= 50) return 'Correct';
  return 'Moyen';
};

/**
 * Get LOS direction label
 */
export const getLOSLabel = (clearCount) => {
  if (clearCount === 8) return 'Dégagement total';
  if (clearCount >= 6) return 'Très bon dégagement';
  if (clearCount >= 4) return 'Dégagement partiel';
  if (clearCount >= 2) return 'Dégagement limité';
  return 'Obstrué';
};
